import { useValue } from "@legendapp/state/react";
import { AvatarDisplay } from "@repo/ui/components/avatar-display";
import { AvatarPicker } from "@repo/ui/components/avatar-picker";
import { type BaseIconName, Icon } from "@repo/ui/components/icon";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@repo/ui/input-group";
import { useMatch } from "@tanstack/react-router";

import { useBoardCapabilities } from "~/pages/board/board-session";
import { boards$ } from "~/stores/boards";
import { datasets$ } from "~/stores/datasets";
import { threads$ } from "~/stores/threads";

export const PageTitle = () => {
  const boardMatch = useMatch({
    from: "/boards/$boardId",
    shouldThrow: false,
  });
  const datasetMatch = useMatch({
    from: "/_app/datasets/$datasetId",
    shouldThrow: false,
  });
  const datasetsBrowseMatch = useMatch({
    from: "/_app/datasets/",
    shouldThrow: false,
  });
  const threadMatch = useMatch({
    from: "/_app/thread/$threadId",
    shouldThrow: false,
  });

  if (boardMatch && boardMatch.status !== "notFound") {
    return <BoardTitle boardId={boardMatch.params.boardId} />;
  }
  if (datasetMatch)
    return <DatasetTitle datasetId={datasetMatch.params.datasetId} />;
  if (datasetsBrowseMatch)
    return <StaticTitle icon="dot-grid" label="Catalog" />;
  if (threadMatch)
    return <ThreadTitle threadId={threadMatch.params.threadId} />;

  return null;
};

/**
 * Non-editable title for list pages that have no single entity to name. Mirrors
 * the icon + label rhythm of the editable titles without the input affordance —
 * including the `md:` step down, which the editable titles inherit from `Input`.
 */
const StaticTitle = ({
  icon,
  label,
}: {
  icon: BaseIconName;
  label: string;
}) => (
  <div className="flex min-w-0 items-center gap-2 px-2">
    <Icon name={icon} className="size-4 text-muted-foreground" />
    <span className="truncate font-medium text-sm md:text-xs/relaxed">
      {label}
    </span>
  </div>
);

const BoardAvatarPicker = ({
  boardId,
  editable,
}: {
  boardId: string;
  editable: boolean;
}) => {
  const avatar = useValue(boards$[boardId]?.avatar);

  if (!editable) {
    return (
      <InputGroupAddon align="inline-start">
        <InputGroupButton size="icon-xs" disabled className="opacity-100">
          <AvatarDisplay avatar={avatar} fallbackIcon="dashboard" />
        </InputGroupButton>
      </InputGroupAddon>
    );
  }

  return (
    <InputGroupAddon align="inline-start">
      <AvatarPicker
        value={avatar}
        onChange={(a) => boards$[boardId].avatar.set(a)}
      >
        <InputGroupButton size="icon-xs">
          <AvatarDisplay avatar={avatar} fallbackIcon="dashboard" />
        </InputGroupButton>
      </AvatarPicker>
    </InputGroupAddon>
  );
};

const BoardTitle = ({ boardId }: { boardId: string }) => {
  const name = useValue(boards$[boardId].name);
  const { canEditBoardMeta } = useBoardCapabilities();

  return (
    <InputGroup variant="transparent" className="w-auto min-w-32 max-w-full">
      <BoardAvatarPicker boardId={boardId} editable={canEditBoardMeta} />
      <InputGroupInput
        placeholder="Untitled board"
        value={name ?? ""}
        onChange={
          canEditBoardMeta
            ? (e) => boards$[boardId].name.set(e.target.value)
            : undefined
        }
        readOnly={!canEditBoardMeta}
        tabIndex={canEditBoardMeta ? undefined : -1}
        className={
          canEditBoardMeta
            ? "field-sizing-content min-w-0 overflow-hidden text-ellipsis"
            : "field-sizing-content min-w-0 cursor-default overflow-hidden text-ellipsis"
        }
      />
    </InputGroup>
  );
};

const DatasetAvatarPicker = ({ datasetId }: { datasetId: string }) => {
  const avatar = useValue(datasets$[datasetId]?.avatar);
  return (
    <InputGroupAddon align="inline-start">
      <AvatarPicker
        value={avatar}
        onChange={(a) => datasets$[datasetId].avatar.set(a)}
      >
        <InputGroupButton size="icon-xs">
          <AvatarDisplay avatar={avatar} fallbackIcon="dataset" />
        </InputGroupButton>
      </AvatarPicker>
    </InputGroupAddon>
  );
};

const DatasetTitle = ({ datasetId }: { datasetId: string }) => {
  const name = useValue(datasets$[datasetId].name);

  return (
    <InputGroup variant="transparent" className="w-auto min-w-32 max-w-full">
      <DatasetAvatarPicker datasetId={datasetId} />
      <InputGroupInput
        placeholder="Untitled dataset"
        value={name ?? ""}
        onChange={(e) => datasets$[datasetId].name.set(e.target.value)}
        className="field-sizing-content min-w-0 overflow-hidden text-ellipsis"
      />
    </InputGroup>
  );
};

const ThreadTitle = ({ threadId }: { threadId: string }) => {
  const title = useValue(threads$[threadId].title);
  // A brand-new thread (local nanoid) doesn't exist server-side until the first
  // message; editing would enqueue an update against a missing thread. Stay
  // read-only until the thread is known.
  const exists = useValue(() => threads$[threadId].id.get() !== undefined);

  return (
    <InputGroup variant="transparent" className="w-auto min-w-32 max-w-full">
      <InputGroupAddon align="inline-start">
        <InputGroupButton size="icon-xs" disabled className="opacity-100">
          <Icon name="comment" />
        </InputGroupButton>
      </InputGroupAddon>
      <InputGroupInput
        placeholder="New thread"
        value={title ?? ""}
        onChange={
          exists
            ? (e) => threads$[threadId].title.set(e.target.value)
            : undefined
        }
        readOnly={!exists}
        tabIndex={exists ? undefined : -1}
        className={
          exists
            ? "field-sizing-content min-w-0 overflow-hidden text-ellipsis"
            : "field-sizing-content min-w-0 cursor-default overflow-hidden text-ellipsis"
        }
      />
    </InputGroup>
  );
};
